import React, { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { User, Upload, Lock, Eye, EyeOff, Monitor, LogOut, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Przeglądarka';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  let os = 'System';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  return `${browser} / ${os}`;
};

export const Profile = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const loginTime = localStorage.getItem('login_time');

  // Password change
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Proszę wybrać plik obrazu'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/users/${user.id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarPreview(response.data.avatar_url);
      toast.success('Avatar zaktualizowany');
      const updatedUser = { ...user, avatar_url: response.data.avatar_url };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload();
    } catch { toast.error('Błąd podczas wgrywania avatara'); } finally { setUploading(false); }
  };

  const validatePassword = (pw) => {
    if (pw.length < 8) return 'Hasło musi mieć minimum 8 znaków';
    if (!/[A-Z]/.test(pw)) return 'Przynajmniej 1 duża litera';
    if (!/[0-9]/.test(pw)) return 'Przynajmniej 1 cyfra';
    return null;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const err = validatePassword(pwForm.newPw);
    if (err) { toast.error(err); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Hasła nie są takie same'); return; }
    setChangingPw(true);
    try {
      await axios.put(`${API_URL}/users/me/password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw
      });
      toast.success('Hasło zmienione pomyślnie');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zmiany hasła');
    } finally { setChangingPw(false); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12">
        <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight mb-8">Profil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Avatar */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6">
            <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-6">Avatar</h2>
            <div className="flex flex-col items-center gap-4">
              <div className="w-32 h-32 rounded-full bg-[var(--crm-bg-btn-secondary)] flex items-center justify-center overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-16 h-16 text-[var(--crm-text-secondary)]" />
                )}
              </div>
              <div className="w-full">
                <Label htmlFor="avatar-upload"
                  className="cursor-pointer flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white py-2 px-4 rounded-md transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Wgrywanie...' : 'Wybierz zdjęcie'}
                </Label>
                <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarUpload} disabled={uploading} className="hidden" />
                <p className="text-[var(--crm-text-secondary)] text-xs mt-2 text-center">JPG, PNG lub GIF (max 5MB)</p>
              </div>
            </div>
          </Card>

          {/* Info */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-2">
            <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-6">Informacje</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Email</Label>
                <p className="text-[var(--crm-text-primary)] text-lg">{user?.email}</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Inicjały</Label>
                <p className="text-[var(--crm-text-primary)] text-lg">{user?.initials}</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Rola</Label>
                <p className="text-[var(--crm-text-primary)] text-lg">{
                  {'admin':'Administrator','user':'Użytkownik','handlowiec_b2b':'Handlowiec B2B','montaz':'Montaż','asystent_biura':'Asystent biura','guest':'Gość','duch':'Duch (Demo)'}[user?.role] || user?.role
                }</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Data utworzenia</Label>
                <p className="text-[var(--crm-text-primary)] text-lg">{new Date(user?.created_at).toLocaleDateString('pl-PL')}</p>
              </div>
            </div>
          </Card>

          {/* Commission Info - read-only for handlowiec_b2b */}
          {(user?.commission_percent || user?.monthly_plan || user?.role === 'handlowiec_b2b') && (
            <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-3" data-testid="profile-commission-card">
              <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4">Prowizja i plan sprzedaży</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-[var(--crm-text-secondary)] text-sm">Prowizja</Label>
                  <p className="text-[var(--crm-text-primary)] text-lg font-mono">{user?.commission_percent ? `${user.commission_percent}%` : '—'}</p>
                </div>
                <div>
                  <Label className="text-[var(--crm-text-secondary)] text-sm">Baza prowizji</Label>
                  <p className="text-[var(--crm-text-primary)] text-lg">{user?.commission_base === 'total_offer' ? 'Od wartości oferty' : user?.commission_base === 'net_margin' ? 'Od marży netto' : user?.commission_base === 'margin' ? 'Od marży' : '—'}</p>
                </div>
                <div>
                  <Label className="text-[var(--crm-text-secondary)] text-sm">Plan miesięczny</Label>
                  <p className="text-[var(--crm-text-primary)] text-lg font-mono">{user?.monthly_plan ? `${user.monthly_plan.toLocaleString('pl-PL')} zł` : '—'}</p>
                </div>
                <div>
                  <Label className="text-[var(--crm-text-secondary)] text-sm">Plan kwartalny</Label>
                  <p className="text-[var(--crm-text-primary)] text-lg font-mono">{user?.quarterly_plan ? `${user.quarterly_plan.toLocaleString('pl-PL')} zł` : '—'}</p>
                </div>
              </div>
              <p className="text-[var(--crm-text-muted)] text-xs mt-3">Te ustawienia może zmienić tylko administrator.</p>
            </Card>
          )}

          {/* Password Change */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-3">
            <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-6 flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#FF6B35]" />
              Zmiana hasła
            </h2>
            <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Obecne hasło *</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={pwForm.current}
                    onChange={e => setPwForm({...pwForm, current: e.target.value})}
                    data-testid="profile-current-pw" required
                    className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)]">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Nowe hasło *</Label>
                <Input type={showPw ? 'text' : 'password'} value={pwForm.newPw}
                  onChange={e => setPwForm({...pwForm, newPw: e.target.value})}
                  data-testid="profile-new-pw" required
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                {pwForm.newPw && validatePassword(pwForm.newPw) && (
                  <p className="text-amber-400 text-xs mt-1">{validatePassword(pwForm.newPw)}</p>
                )}
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Powtórz nowe hasło *</Label>
                <Input type={showPw ? 'text' : 'password'} value={pwForm.confirm}
                  onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                  data-testid="profile-confirm-pw" required
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
                {pwForm.newPw && pwForm.confirm && pwForm.newPw !== pwForm.confirm && (
                  <p className="text-red-400 text-xs mt-1">Hasła nie są takie same</p>
                )}
              </div>
              <Button type="submit" disabled={changingPw} data-testid="profile-change-pw-btn"
                className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
                {changingPw ? 'Zmieniam...' : 'Zmień hasło'}
              </Button>
            </form>
          </Card>

          {/* Theme / Wygląd */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-3" data-testid="theme-card">
            <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
              <Sun className="w-5 h-5 text-[#FF6B35]" />
              Wygląd
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'light', label: 'Jasny', icon: Sun, desc: 'Białe tło, ciemny tekst' },
                { value: 'dark', label: 'Ciemny', icon: Moon, desc: 'Granatowe tło, jasny tekst' },
                { value: 'system', label: 'Jak w systemie', icon: Monitor, desc: 'Automatycznie wg systemu' },
              ].map(opt => {
                const Icon = opt.icon;
                const active = theme === opt.value;
                return (
                  <button key={opt.value} onClick={() => {
                    setTheme(opt.value);
                    axios.put(`${API_URL}/users/me/theme`, { theme: opt.value }).catch(() => {});
                  }}
                    data-testid={`theme-option-${opt.value}`}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      active
                        ? 'border-[#FF6B35] bg-[#FF6B35]/10'
                        : 'border-[var(--crm-border)] hover:border-[var(--crm-text-muted)]'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${active ? 'text-[#FF6B35]' : 'text-[var(--crm-text-muted)]'}`} />
                    <p className={`font-semibold text-sm ${active ? 'text-[#FF6B35]' : 'text-[var(--crm-text-primary)]'}`}>{opt.label}</p>
                    <p className="text-[var(--crm-text-muted)] text-xs mt-0.5">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Active Session */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-3" data-testid="session-info-card">
            <h2 className="text-xl font-manrope font-semibold text-[var(--crm-text-primary)] mb-6 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-[#FF6B35]" />
              Aktywna sesja
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Zalogowano</Label>
                <p className="text-[var(--crm-text-primary)]">{loginTime ? new Date(loginTime).toLocaleString('pl-PL') : '—'}</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Urządzenie</Label>
                <p className="text-[var(--crm-text-primary)]">{getDeviceInfo()}</p>
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)] text-sm">Ostatnie logowanie (serwer)</Label>
                <p className="text-[var(--crm-text-primary)]">{user?.last_login ? new Date(user.last_login).toLocaleString('pl-PL') : '—'}</p>
              </div>
            </div>
            <Button onClick={() => { logout(); window.location.href = '/login'; }} data-testid="profile-logout-btn"
              className="bg-red-600/80 hover:bg-red-600 text-white">
              <LogOut className="w-4 h-4 mr-2" />Wyloguj się
            </Button>
          </Card>
        </div>
      </div>
    </Layout>
  );
};
